/**
 * Contact Page for trAInedup.ai
 * Provides multiple ways for users to get in touch
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, MessageCircle, Send, Instagram, Facebook, Youtube } from 'lucide-react';
import { Layout } from '../components/common/Layout';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { Input } from '../components/common/Input';
import { ROUTES } from '../utils/constants';
import { OPEN_CHATBOT_EVENT } from '../components/chat/SubscriptionChatbot';
import { SEOHead } from '../components/seo/SEOHead';

/**
 * Contact page component with contact form and information
 * Includes multiple contact methods and support options
 */
export const ContactPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // TODO: Implement actual form submission
    // This would typically send to a backend API or email service
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubmitting(false);
    // Reset form or show success message
    alert('Thank you for your message! We\'ll get back to you soon.');
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  /**
   * Handle opening the chatbot when "Start Chat" is clicked
   */
  const handleStartChat = () => {
    // Dispatch custom event to open the chatbot
    window.dispatchEvent(new Event(OPEN_CHATBOT_EVENT));
  };

  return (
    <Layout>
      <SEOHead
        title="Contact Us - Get Help with Your AI Learning Journey"
        description="Have questions about AI training? Our friendly team is here to help. Contact trAInedup.ai for support, inquiries, or to learn more about our personalized AI learning platform."
        canonical="https://trainedup.ai/contact"
        keywords={['contact trAInedup', 'AI training support', 'AI learning help', 'customer support']}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-secondary-50 py-20">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-montserrat font-bold text-4xl md:text-5xl text-neutral-800 mb-6">
              Get in Touch
            </h1>
            <p className="text-xl text-neutral-600 mb-8">
              Do you have questions around your AI journey? Do you want to know if trAInedup.ai is right for you?
              We're here to help you succeed with your journey.
            </p>
            <p className="text-lg text-neutral-600">
              Our friendly team is always ready to assist you on your AI learning journey.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8 mb-16">
              {/* Email */}
              <Card className="text-center" hover>
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Mail className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-montserrat font-semibold text-lg mb-2">Email Us</h3>
                <p className="text-neutral-600 mb-4">
                  For general inquiries and support
                </p>
                <a 
                  href="mailto:support@trainedup.ai" 
                  className="text-primary hover:text-primary-600 font-medium"
                >
                  support@trainedup.ai
                </a>
              </Card>

              {/* Live Chat */}
              <Card className="text-center" hover>
                <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <MessageCircle className="w-8 h-8 text-secondary" />
                </div>
                <h3 className="font-montserrat font-semibold text-lg mb-2">Live Chat</h3>
                <p className="text-neutral-600 mb-4">
                  Learn more from our AI assistant
                </p>
                <button 
                  onClick={handleStartChat}
                  className="text-secondary hover:text-secondary-600 font-medium"
                >
                  Start Chat
                </button>
              </Card>
            </div>

            {/* Contact Form and Info */}
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <Card>
                <h2 className="font-montserrat font-bold text-2xl text-neutral-800 mb-6">
                  Send us a Message
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input
                      label="Your Name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="John Smith"
                    />
                    <Input
                      label="Email Address"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="john@example.com"
                    />
                  </div>
                  
                  <Input
                    label="Subject"
                    name="subject"
                    type="text"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    placeholder="How can we help you?"
                  />
                  
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Message
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                      placeholder="Tell us more about your question or how we can help..."
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    fullWidth
                    loading={isSubmitting}
                    icon={Send}
                    iconPosition="right"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </Card>

              {/* Additional Information */}
              <div className="space-y-8">
                <Card>
                  <h3 className="font-montserrat font-semibold text-xl text-neutral-800 mb-4">
                    Frequently Asked Questions
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-neutral-800 mb-2">
                        How quickly will I hear back?
                      </h4>
                      <p className="text-neutral-600 text-sm">
                        We typically respond to all inquiries within 24 hours during business days.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-medium text-neutral-800 mb-2">
                        Can I schedule a demo?
                      </h4>
                      <p className="text-neutral-600 text-sm">
                        If you are a corporate user, once you have submitted your request form, we will contact you to discuss further.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-medium text-neutral-800 mb-2">
                        Do you offer custom training?
                      </h4>
                      <p className="text-neutral-600 text-sm">
                        All members receive a bespoke training plan, after their Skills Assessment or consultation.
                      </p>
                    </div>
                  </div>
                </Card>

                <Card>
                  <h3 className="font-montserrat font-semibold text-xl text-neutral-800 mb-4">
                    Support Hours
                  </h3>
                  <div className="space-y-2 text-neutral-600">
                    <div className="flex justify-between">
                      <span>Monday - Friday:</span>
                      <span>9:00 AM - 6:00 PM GMT</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Saturday and Sunday:</span>
                      <span>Closed</span>
                    </div>
                  </div>
                  <p className="text-sm text-neutral-500 mt-4">
                    Emergency support available 24/7 for Corporate customers
                  </p>
                </Card>

                <Card>
                  <h3 className="font-montserrat font-semibold text-xl text-neutral-800 mb-4">
                    Other Ways to Connect
                  </h3>
                  <div className="space-y-3">
                    <a
                      href="https://www.instagram.com/trained.up/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-neutral-600 hover:text-primary transition-colors duration-200"
                    >
                      <Instagram className="w-5 h-5 mr-3" />
                      Join us on Instagram
                    </a>
                    <a
                      href="https://www.facebook.com/trained.up/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-neutral-600 hover:text-primary transition-colors duration-200"
                    >
                      <Facebook className="w-5 h-5 mr-3" />
                      Join us on Facebook
                    </a>
                    <a
                      href="https://discord.com/invite/WUHyEZHnS9"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-neutral-600 hover:text-primary transition-colors duration-200"
                    >
                      <MessageCircle className="w-5 h-5 mr-3" />
                      Join us on Discord
                    </a>
                    <a
                      href="https://www.youtube.com/@trAIned_up"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-neutral-600 hover:text-primary transition-colors duration-200"
                    >
                      <Youtube className="w-5 h-5 mr-3" />
                      Subscribe to our YouTube channel
                    </a>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary to-secondary">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="font-montserrat font-bold text-3xl md:text-4xl mb-6">
              Ready to Start Learning?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Don't wait for a perfect moment. Take our free Skills Assessment to begin 
              your AI journey today.
            </p>
            
            <Button
              variant="primary"
              size="lg"
              onClick={() => navigate(ROUTES.skillsAssessment)}
            >
              Unlock Your Learning Plan
            </Button>

            <p className="mt-6 text-sm opacity-75">
              No credit card required • Get personalised recommendations in minutes
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
};
